(() => {
  'use strict';
  const KIT_FAMILIES = [
    { handle: 'mold-kit-required-1', one: '44218307018989', two: '44218307051757' },
    { handle: 'starter-molding-kit', one: '39439197405336', two: '39439197601944' },
    { handle: 'starting-mold-kit', one: '39456451887256', two: '39456451952792' }
  ];
  const KIT_IDS = new Set(KIT_FAMILIES.flatMap(family => [family.one, family.two]));
  // Pure display arithmetic. SC remains the sole owner of real cart charges.
  function completePrice(base, minimumKit, charges) {
    if (!Number.isSafeInteger(base) || base < 0 || !Number.isSafeInteger(minimumKit) || minimumKit < 0) return null;
    let selected = 0, kit = 0;
    for (const charge of charges) {
      if (!Number.isSafeInteger(charge.price) || charge.price < 0 || !Number.isSafeInteger(charge.quantity) || charge.quantity < 1) return null;
      const amount = charge.price * charge.quantity;
      selected += amount;
      if (KIT_IDS.has(String(charge.id))) kit += amount;
    }
    const requiredKit = Math.max(kit, minimumKit);
    return { total: base + selected - kit + requiredKit, base, kit: requiredKit, extras: selected - kit };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { completePrice };
  if (typeof customElements === 'undefined' || customElements.get('cg-complete-price')) return;
  const listeners = new WeakMap();
  function subscribe(optionProduct, element) {
    let subscribers = listeners.get(optionProduct);
    if (!subscribers) {
      subscribers = new Set();
      listeners.set(optionProduct, subscribers);
      // SC's removeListener removes all listeners for an event; never call it.
      for (const event of ['total_changed', 'price_updated', 'options_loaded']) {
        optionProduct.ee.on(event, () => { for (const entry of subscribers) entry.refresh(); });
      }
    }
    subscribers.add(element);
    return () => subscribers.delete(element);
  }
  class CompletePrice extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.bindAttempts = 0;
      this.gateMessage = 'Choose the two-mold kit for this top-and-bottom grill.';
      window.addEventListener('submit', event => {
        if (event.target === this.form) this.guardPurchase(event);
      }, { capture: true, signal: this.abort.signal });
      window.addEventListener('click', event => {
        const button = event.target.closest?.('button, input[type="submit"], input[type="image"], .bold_clone, .shopify-payment-button__button');
        if (!button || !this.belongsToForm(button)) return;
        const type = (button.getAttribute('type') || 'submit').toLowerCase();
        if (type === 'submit' || type === 'image' || button.name === 'add' || button.matches('.bold_clone, .shopify-payment-button__button')) this.guardPurchase(event);
      }, { capture: true, signal: this.abort.signal });
      this.format = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: this.dataset.currency || 'USD'
      });
      document.addEventListener('change', event => {
        const form = this.form;
        if (form && (event.target.form === form || form.contains(event.target))) this.queueRefresh();
      }, { signal: this.abort.signal });
      this.form?.addEventListener('variant:change', () => this.queueRefresh(), { signal: this.abort.signal });
      this.refresh();
      this.bindOptions();
    }
    disconnectedCallback() {
      this.restoreKitOption();
      this.validityInput?.remove();
      this.abort?.abort();
      this.unsubscribe?.();
      clearTimeout(this.bindTimer);
      clearTimeout(this.refreshTimer);
    }
    get form() { return document.getElementById(this.getAttribute('form')); }
    belongsToForm(node) {
      const form = this.form;
      return Boolean(form && (node.form === form || form.contains(node) || node.getAttribute('form') === form.id));
    }
    get enforcesTwoMolds() {
      return this.dataset.enforceKitRows === 'true' && Number(this.dataset.minimumKit) >= 2200;
    }
    kitControl() {
      const matches = Array.from(this.form?.querySelectorAll('select[name="properties[Mold Kit (Required):]"]') || []);
      for (const select of matches) {
        for (const family of KIT_FAMILIES) {
          const one = select.querySelector(`option[data-product-handle="${family.handle}"][data-option_value_key="0"]`);
          const two = select.querySelector(`option[data-product-handle="${family.handle}"][data-option_value_key="1"]`);
          if (one && two && select.querySelector('option[value=""]')) return { select, one, two, family };
        }
      }
      return null;
    }
    restoreKitOption() {
      if (this.disabledKitOption) this.disabledKitOption.disabled = false;
      this.disabledKitOption = null;
    }
    updateKitGate(charges) {
      if (this.clearingKit) return false;
      const active = this.enforcesTwoMolds;
      if (!active) {
        this.restoreKitOption();
        this.setGateState(true, false);
        return true;
      }
      const control = this.kitControl();
      if (control) {
        if (this.disabledKitOption && this.disabledKitOption !== control.one) this.restoreKitOption();
        if (!control.one.disabled) { control.one.disabled = true; this.disabledKitOption = control.one; }
        if (control.select.value === control.one.value) {
          this.clearingKit = true;
          control.select.value = '';
          // Use the normal input event path. SC removes its own old charge; we never write its hidden fields.
          control.select.dispatchEvent(new Event('change', { bubbles: true }));
          this.clearingKit = false;
          this.queueRefresh();
        }
      }
      const selectedKit = (charges || []).filter(charge => KIT_IDS.has(String(charge.id)));
      const valid = Boolean(control && control.select.value === control.two.value && !control.two.disabled && selectedKit.length === 1 && String(selectedKit[0].id) === control.family.two && selectedKit[0].price === 2200 && selectedKit[0].quantity === 1);
      this.setGateState(valid, true);
      return valid;
    }
    setGateState(valid, active) {
      this.dataset.kitState = active ? (valid ? 'ready' : 'choose-two-molds') : 'unrestricted';
      let note = this.querySelector('[data-complete-kit-error]');
      if (!note && active) {
        note = document.createElement('p');
        note.setAttribute('data-complete-kit-error', '');
        note.setAttribute('role', 'status');
        this.append(note);
      }
      if (note) { note.textContent = this.gateMessage; note.hidden = !active || valid; }
      if (!this.validityInput && active) {
        const input = document.createElement('input');
        input.type = 'text'; input.hidden = true; input.tabIndex = -1;
        input.setAttribute('form', this.getAttribute('form'));
        input.setAttribute('data-complete-kit-validity', '');
        input.addEventListener('invalid', event => { event.preventDefault(); this.kitControl()?.select.focus(); });
        this.append(input); this.validityInput = input;
      }
      this.validityInput?.setCustomValidity(active && !valid ? this.gateMessage : '');
    }
    guardPurchase(event) {
      if (!this.isConnected || !this.enforcesTwoMolds) return;
      if (this.updateKitGate(this.readCharges())) return;
      event.preventDefault(); event.stopImmediatePropagation();
      this.kitControl()?.select.focus();
    }
    queueRefresh() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), 0);
    }
    bindOptions() {
      if (!this.isConnected) return;
      const app = window.BOLD?.options?.app;
      const optionProduct = this.form && app?.getOptionProductByForm?.(this.form);
      if (optionProduct?.ee) {
        this.unsubscribe = subscribe(optionProduct, this);
        this.refresh();
      } else if (++this.bindAttempts < 120) {
        this.bindTimer = setTimeout(() => this.bindOptions(), 250);
      }
    }
    readCharges() {
      const form = this.form;
      if (!form) return [];
      const field = name => form.querySelector(`[name="properties[${name}]"]`)?.value || '';
      const ids = field('_boldVariantIds');
      if (!ids) return [];
      const prices = field('_boldVariantPrices').split(',');
      const quantities = field('_boldVariantQtys');
      const quantityList = quantities ? quantities.split(',') : [];
      const idList = ids.split(',');
      if (prices.length !== idList.length || (quantityList.length && quantityList.length !== idList.length)) return null;
      return idList.map((id, index) => ({ id, price: Number(prices[index]), quantity: quantityList.length ? Number(quantityList[index]) : 1 }));
    }
    setMoney(node, cents) {
      if (!node) return;
      const label = node.querySelector('.sr-only');
      node.replaceChildren(...(label ? [label] : []), document.createTextNode(this.format.format(cents / 100)));
    }
    refresh() {
      if (!this.isConnected) return;
      const charges = this.readCharges();
      this.updateKitGate(charges);
      const result = charges && completePrice(Number(this.dataset.nativePrice), Number(this.dataset.minimumKit), charges);
      if (!result) { this.dataset.priceState = 'unavailable'; return; }
      this.dataset.priceState = 'ready';
      this.setMoney(this.querySelector('sale-price'), result.total);
      const reference = Number(this.dataset.compareAt);
      if (reference > result.base) this.setMoney(this.querySelector('compare-at-price'), reference + result.kit + result.extras);
      const breakdown = this.querySelector('[data-complete-breakdown]');
      if (breakdown) breakdown.textContent = `Grill ${this.format.format(result.base / 100)} + required mold kit ${this.format.format(result.kit / 100)}${result.extras ? ` + selected extras ${this.format.format(result.extras / 100)}` : ''}. Charged as separate items.`;
    }
  }
  customElements.define('cg-complete-price', CompletePrice);
})();
